import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, ShieldCheck } from 'lucide-react';

export default function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-stone-900 text-stone-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="space-y-6">
            <Link to="/" className="inline-block group">
              <div className="flex items-center gap-3">
                <img 
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=240,quality=90,format=auto/assets/6015610c-faa3-4021-bc7e-a866f30ca512/021a31cb-25cf-47cf-a88c-008b05293be5.png" 
                  alt="Entreprenør Jan Hedegaard logo - Autoriseret kloakmester i Galten" 
                  className="h-16 lg:h-[120px] w-auto transition-all duration-300 group-hover:scale-110 brightness-0 invert"
                />
              </div>
            </Link>
            <p className="text-sm leading-relaxed">
              Professionel kloakmester med fokus på kvalitet, ordentlighed og langsigtede løsninger til erhverv og private.
            </p>
            <div className="space-y-2 pt-1 text-sm text-stone-300">
              <div className="flex items-center gap-2 font-medium text-white">
                <ShieldCheck className="w-5 h-5 text-orange-500 shrink-0" />
                <span>Autoriseret Kloakmester</span>
              </div>
              <p className="text-xs text-stone-400 pl-7 leading-relaxed">
                Autorisation via Sikkerhedsstyrelsen &middot; CVR: 45789942
              </p>
            </div>

            <div className="pt-1 text-xs text-stone-300">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-stone-800/80 border border-stone-700/60">
                <span>⭐</span>
                <span>
                  <strong className="text-white font-medium">5 / 5 stjerner</strong> baseret på anmeldelser på{' '}
                  <a
                    href="https://www.3byggetilbud.dk/leverandoer/entreprenr-jan-hedegaard-aps/"
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="text-orange-400 font-medium underline underline-offset-2 hover:text-orange-300 transition-colors"
                  >
                    3byggetilbud.dk
                  </a>
                </span>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <p className="text-white font-bold mb-6">Ydelser</p>
            <ul className="space-y-4 text-sm">
              <li><Link to="/kloakmester-arbejde" className="hover:text-orange-500 transition-colors">Kloakmesterarbejde</Link></li>
              <li><Link to="/entreprenoerarbejde" className="hover:text-orange-500 transition-colors">Entreprenørarbejde</Link></li>
              <li><Link to="/kloakservice" className="hover:text-orange-500 transition-colors">Kloakservice</Link></li>
              <li><Link to="/kloakseparering" className="hover:text-orange-500 transition-colors">Kloakseparering</Link></li>
              <li><Link to="/tv-inspektion" className="hover:text-orange-500 transition-colors">TV-inspektion</Link></li>
              <li><Link to="/anlaeg-og-belaegning" className="hover:text-orange-500 transition-colors">Anlæg og belægning</Link></li>
              <li><Link to="/stoebning-af-beton" className="hover:text-orange-500 transition-colors">Støbning af beton</Link></li>
              <li><Link to="/haveservice" className="hover:text-orange-500 transition-colors">Haveservice</Link></li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <p className="text-white font-bold mb-6">Kontakt</p>
            <ul className="space-y-4 text-sm">
              <li className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-orange-500 shrink-0" />
                <a href="tel:+4522458567" className="hover:text-orange-500 transition-colors">22 45 85 67</a>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-orange-500 shrink-0" />
                <a href="mailto:jan@janhedegaard.dk" className="hover:text-orange-500 transition-colors">jan@janhedegaard.dk</a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-orange-500 shrink-0" />
                <span>Søballevej 31, 8464 Galten</span>
              </li>
            </ul>
          </div>

          {/* Opening Hours */}
          <div>
            <p className="text-white font-bold mb-6">Åbningstider</p>
            <ul className="space-y-4 text-sm">
              <li className="flex justify-between">
                <span>Mandag - Torsdag:</span>
                <span className="text-white">07:00 - 16:00</span>
              </li>
              <li className="flex justify-between">
                <span>Fredag:</span>
                <span className="text-white">07:00 - 14:00</span>
              </li>
              <li className="flex justify-between">
                <span>Lørdag - Søndag:</span>
                <span className="text-white">Lukket</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Geografisk dækningsområde (Lokal SEO) */}
        <div className="mt-12 pt-6 border-t border-stone-800/80 text-xs text-stone-400 text-center md:text-left leading-relaxed">
          <p>
            <span className="text-stone-300 font-medium">Dækningsområde i Østjylland:</span> Vi udfører autoriseret kloakmesterarbejde, kloakservice og entreprenøropgaver i{' '}
            <span className="text-stone-300">Galten</span>,{' '}
            <span className="text-stone-300">Skanderborg</span>,{' '}
            <span className="text-stone-300">Ry</span>,{' '}
            <span className="text-stone-300">Harlev</span>,{' '}
            <span className="text-stone-300">Låsby</span>,{' '}
            <span className="text-stone-300">Skovby</span>,{' '}
            <span className="text-stone-300">Hørning</span>,{' '}
            <span className="text-stone-300">Hammel</span>,{' '}
            <span className="text-stone-300">Silkeborg</span> og{' '}
            <span className="text-stone-300">Aarhus</span> samt det øvrige Østjylland.
          </p>
        </div>

        <div className="mt-6 pt-6 border-t border-stone-800 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-stone-500">
          <p>© {currentYear} Entreprenør Jan Hedegaard ApS. Alle rettigheder forbeholdes.</p>
          <div className="flex flex-wrap items-center gap-4 sm:gap-6">
            <span className="font-medium text-stone-400">CVR: 45789942</span>
            <span className="font-medium text-stone-400">Aut. Kloakmester</span>
            <span>EAN: 5790002848186</span>
            <a href="https://www.iubenda.com/privacy-policy/15031924" className="iubenda-white iubenda-noiframe iubenda-embed hover:text-stone-300 transition-colors" title="Privatlivspolitik ">Privatlivspolitik</a>
            <a href="https://www.iubenda.com/privacy-policy/15031924/cookie-policy" className="iubenda-white iubenda-noiframe iubenda-embed hover:text-stone-300 transition-colors" title="Cookiepolitik ">Cookiepolitik</a>
            <Link to="/kontakt" className="hover:text-stone-300">Kontakt os</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
